import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch, descargarArchivo } from '../../lib/api';
import { Spinner } from '../../components/Spinner';
import { ETAPAS, type PipelineJob } from '../../jobs/JobsContext';
import { agente } from '../../lib/agentes';

/**
 * Corrida en vivo — el stepper auditor del orquestador: muestra a los agentes
 * trabajando e interactuando (fuentes → N1 → N2 → N3⇄N4 → N5 → noticia), y por
 * cada llamada LLM: qué recibió, qué devolvió, cuántos tokens consumió y el
 * razonamiento del modelo cuando el proveedor lo expone.
 */

interface AgentRun {
  id: string;
  agent_code: string;
  provider_code: string;
  model: string;
  tokens_in: number | null;
  tokens_out: number | null;
  tokens_reasoning: number | null;
  est_cost: number | null;
  latency_ms: number | null;
  repairs: number;
  success: boolean | null;
  error: string | null;
  input_excerpt: string | null;
  output: unknown;
  reasoning: string | null;
  created_at: string;
}

interface FuenteDetalle {
  code: string;
  nombre: string;
  ok: boolean;
  chars: number;
  error: string | null;
  /** false = descargó bien pero N1 no pudo analizarla (falla LLM): la corrida está incompleta. */
  analizada?: boolean;
  error_analisis?: string | null;
}

interface FallaLLM {
  agente: string;
  fuente?: string;
  modelo: string;
  error: string;
  tipo: 'fallo' | 'rescate';
}

const PASOS = [
  { key: 'fuentes', label: 'Fuentes', mono: 'F' },
  { key: 'N1', label: 'Nicanor · Lector', mono: 'N1' },
  { key: 'N2', label: 'Marcela · Editora', mono: 'N2' },
  { key: 'N3N4', label: 'Violeta ⇄ Gabriela', mono: 'N3·4' },
  { key: 'N5', label: 'Pablo · Creativo', mono: 'N5' },
  { key: 'finalizando', label: 'Noticia', mono: '✓' },
] as const;

const nf = new Intl.NumberFormat('es-CL');
const usd = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 4 });

function indicePaso(job: PipelineJob | null): number {
  if (!job) return -1;
  if (job.status === 'done') return PASOS.length;
  const i = PASOS.findIndex((p) => p.key === job.stage);
  return i === -1 ? (job.status === 'queued' ? -1 : 0) : i;
}

export default function CorridaEnVivo() {
  const { jobId } = useParams<{ jobId: string }>();
  const [job, setJob] = useState<PipelineJob | null>(null);
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [htmlNoticia, setHtmlNoticia] = useState<string | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [noEncontrado, setNoEncontrado] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const consultar = useCallback(async () => {
    if (!jobId) return;
    try {
      const [jd, rd] = await Promise.all([
        apiFetch<{ job: PipelineJob }>(`/api/newsletter/jobs/${jobId}`),
        apiFetch<{ runs: AgentRun[] }>(`/api/newsletter/jobs/${jobId}/runs`),
      ]);
      setJob(jd.job);
      setRuns(rd.runs);
      if (jd.job.status === 'done' || jd.job.status === 'error') {
        if (timer.current) clearInterval(timer.current);
        timer.current = null;
      }
    } catch {
      setNoEncontrado(true);
      if (timer.current) clearInterval(timer.current);
    }
  }, [jobId]);

  useEffect(() => {
    void consultar();
    timer.current = setInterval(() => void consultar(), 2500);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [consultar]);

  // Al terminar: trae el HTML de la noticia para el preview embebido.
  useEffect(() => {
    if (job?.status === 'done' && job.edition_id && !htmlNoticia) {
      void apiFetch<{ edition: { html: string | null } }>(`/api/editions/${job.edition_id}`)
        .then((d) => setHtmlNoticia(d.edition.html))
        .catch(() => undefined);
    }
  }, [job, htmlNoticia]);

  const paso = indicePaso(job);
  const fuentes = (job?.detail?.fuentes as FuenteDetalle[] | undefined) ?? [];
  const n1 = job?.detail?.n1 as { fuentesProcesadas?: number; totalFuentes?: number; itemsExtraidos?: number; fuentes_fallidas?: string[] } | undefined;
  const fuentesFallidas = n1?.fuentes_fallidas ?? [];
  const fallasLLM = (job?.detail?.fallas_llm as FallaLLM[] | undefined) ?? [];
  const n2 = job?.detail?.n2 as { clasificados?: number; descartados?: number; publicables?: number } | undefined;
  const n3n4 = job?.detail?.n3n4 as { itemActual?: number; totalItems?: number; requierenHumano?: number } | undefined;

  const totalIn = runs.reduce((a, r) => a + (r.tokens_in ?? 0), 0);
  const totalOut = runs.reduce((a, r) => a + (r.tokens_out ?? 0), 0);
  const totalCosto = runs.reduce((a, r) => a + Number(r.est_cost ?? 0), 0);
  const corriendo = job?.status === 'queued' || job?.status === 'running';

  if (noEncontrado) {
    return (
      <div className="tarjeta p-8 text-center">
        <p className="text-sm font-normal text-gris">No encontramos esa corrida.</p>
        <Link to="/newsletter" className="btn-secundario mt-4">Volver a Newsletter</Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Encabezado */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="titulo-pagina">Corrida en vivo</h1>
            {job && (
              <span
                className={`chip ${
                  corriendo
                    ? 'bg-celeste/20 text-azul-profundo ring-celeste/60'
                    : job.status === 'done'
                      ? 'bg-emerald-50 text-emerald-700 ring-emerald-300'
                      : 'bg-red-50 text-red-700 ring-red-300'
                }`}
              >
                {corriendo && <Spinner tamano="xs" />}
                {corriendo ? (ETAPAS[job.stage ?? ''] ?? 'En cola') : job.status === 'done' ? 'Completada' : 'Error'}
              </span>
            )}
            {job?.scan_date && job.scan_date !== job.created_at.slice(0, 10) && (
              <span className="chip bg-azul-noche/5 text-azul-noche ring-azul-noche/20">
                corrida histórica · {job.scan_date}
              </span>
            )}
            {job && (
              <span className="chip bg-slate-100 text-slate-600 ring-slate-200">
                {job.generada_por ? `Generada por ${job.generada_por}` : 'Programada · Víctor, el Director'}
              </span>
            )}
          </div>
          <p className="subtitulo-pagina">
            Víctor, el Director, comanda a los agentes; aquí auditas cada llamada, sus tokens y su razonamiento.
          </p>
        </div>
        <div className="flex gap-4 text-right">
          <div>
            <p className="text-xl font-medium leading-none tabular-nums text-azul-profundo">{nf.format(totalIn + totalOut)}</p>
            <p className="mt-1 text-xs font-normal text-gris">tokens ({nf.format(totalIn)} in · {nf.format(totalOut)} out)</p>
          </div>
          <div>
            <p className="text-xl font-medium leading-none tabular-nums text-azul-profundo">{usd.format(totalCosto)}</p>
            <p className="mt-1 text-xs font-normal text-gris">costo estimado</p>
          </div>
        </div>
      </header>

      {/* Stepper */}
      <div className="tarjeta overflow-x-auto p-5">
        <ol className="flex min-w-[560px] items-center">
          {PASOS.map((p, i) => {
            const estado = job?.status === 'error' && i === paso ? 'error' : i < paso ? 'done' : i === paso ? 'activo' : 'pendiente';
            return (
              <li key={p.key} className={`flex items-center ${i < PASOS.length - 1 ? 'flex-1' : ''}`}>
                <div className="flex flex-col items-center gap-1.5">
                  <span
                    className={`relative flex h-10 w-10 items-center justify-center rounded-full text-[11px] font-medium transition-colors duration-300 ${
                      estado === 'done'
                        ? 'bg-azul-profundo text-white'
                        : estado === 'activo'
                          ? 'bg-white text-azul-profundo ring-2 ring-azul-profundo'
                          : estado === 'error'
                            ? 'bg-red-50 text-red-600 ring-2 ring-red-400'
                            : 'bg-slate-100 text-gris'
                    }`}
                  >
                    {estado === 'done' ? (
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m5 13 4 4L19 7" />
                      </svg>
                    ) : (
                      p.mono
                    )}
                    {estado === 'activo' && corriendo && (
                      <span aria-hidden="true" className="absolute -bottom-1 h-[3px] w-6 rounded-full bg-gradient-to-r from-azul-profundo to-celeste" />
                    )}
                  </span>
                  <span className={`whitespace-nowrap text-[11px] font-medium ${estado === 'pendiente' ? 'text-gris' : 'text-azul-noche'}`}>{p.label}</span>
                </div>
                {i < PASOS.length - 1 && (
                  <span aria-hidden="true" className={`mx-2 mb-5 h-px flex-1 ${i < paso ? 'bg-azul-profundo' : 'bg-slate-200'}`} />
                )}
              </li>
            );
          })}
        </ol>

        {/* Contadores de progreso */}
        {(n1 || n2 || n3n4) && (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            {n1 && <span className="chip bg-slate-100 text-slate-700 ring-slate-300">N1: {n1.fuentesProcesadas}/{n1.totalFuentes} fuentes · {n1.itemsExtraidos} items</span>}
            {fuentesFallidas.length > 0 && (
              <span className="chip bg-red-50 text-red-700 ring-red-300">⚠ {fuentesFallidas.join(', ')} sin analizar</span>
            )}
            {n2 && <span className="chip bg-slate-100 text-slate-700 ring-slate-300">N2: {n2.clasificados} clasificados · {n2.descartados} descartados · {n2.publicables} publicables</span>}
            {(() => {
              const cons = job?.detail?.consolidacion as { candidatos?: number; publicables?: number; desde_alertas?: number } | undefined;
              return cons ? (
                <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">
                  Semana consolidada: {cons.publicables ?? 0} items ({cons.desde_alertas ?? 0} desde alertas)
                </span>
              ) : null;
            })()}
            {n3n4 && <span className="chip bg-slate-100 text-slate-700 ring-slate-300">N3⇄N4: item {n3n4.itemActual}/{n3n4.totalItems}{n3n4.requierenHumano ? ` · ${n3n4.requierenHumano} a revisión humana` : ''}</span>}
          </div>
        )}
      </div>

      {/* Fuentes */}
      {fuentes.length > 0 && (
        <div className="tarjeta p-5">
          <p className="text-sm font-medium text-azul-noche">
            Fuentes oficiales <span className="font-normal text-gris">( {fuentes.filter((f) => f.ok).length}/{fuentes.length} conectadas )</span>
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {fuentes.map((f) => {
              // Tres estados: descarga falló (ámbar), descargó pero N1 no la analizó (rojo —
              // la corrida está incompleta para esa fuente), y todo bien (verde).
              const noAnalizada = f.ok && f.analizada === false;
              const titulo = noAnalizada
                ? `Descargó pero NO fue analizada — ${f.error_analisis ?? 'N1 falló'}`
                : f.ok ? `${nf.format(f.chars)} caracteres extraídos` : (f.error ?? 'error');
              const estilo = noAnalizada
                ? 'bg-red-50 text-red-700 ring-red-300'
                : f.ok ? 'bg-emerald-50 text-emerald-700 ring-emerald-300' : 'bg-amber-50 text-amber-700 ring-amber-300';
              return (
                <span key={f.code} title={titulo} className={`chip ${estilo}`}>
                  <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${noAnalizada ? 'bg-red-500' : f.ok ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                  {f.code}
                  {noAnalizada && <span className="font-medium"> · sin analizar</span>}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Feed de auditoría por agente */}
      <div className="tarjeta overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 pb-1 pt-4">
          <p className="text-sm font-medium text-azul-noche">
            Llamadas de agentes <span className="font-normal text-gris">( {runs.length} )</span>
          </p>
          {corriendo && <span className="flex items-center gap-2 text-xs font-normal text-gris"><Spinner tamano="xs" /> actualizando en vivo</span>}
        </div>
        {runs.length === 0 ? (
          <p className="px-5 py-8 text-sm font-normal text-gris">Aún no hay llamadas registradas — el orquestador está descargando las fuentes.</p>
        ) : (
          <ul className="divide-y divide-slate-100 px-2 pb-2">
            {runs.map((r) => {
              const info = agente(r.agent_code);
              return (
                <li key={r.id}>
                  <details className="group px-3 py-2.5">
                    <summary className="flex cursor-pointer list-none flex-wrap items-center gap-3 [&::-webkit-details-marker]:hidden">
                      <span aria-hidden="true" className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold ${info.tinte}`}>{r.agent_code}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium leading-tight text-azul-noche">
                          {info.nombre}
                          {info.oficio && <span className="font-normal text-gris"> · {info.oficio} ({info.code})</span>}
                          {r.repairs > 0 && <span className="ml-2 font-normal text-amber-700">· {r.repairs} reparación{r.repairs > 1 ? 'es' : ''}</span>}
                          {r.success === false && <span className="ml-2 font-normal text-red-600">· falló</span>}
                        </span>
                        <span className="block text-xs font-normal leading-tight text-gris">{r.model} · {nf.format((r.tokens_in ?? 0) + (r.tokens_out ?? 0))} tokens{r.tokens_reasoning ? ` (+${nf.format(r.tokens_reasoning)} pensamiento)` : ''} · {usd.format(Number(r.est_cost ?? 0))} · {nf.format(r.latency_ms ?? 0)} ms</span>
                      </span>
                      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 shrink-0 text-gris transition-transform duration-150 group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </summary>
                    <div className="mt-2 flex flex-col gap-3 rounded-[10px] border border-slate-200 bg-[#FAFBFC] p-4">
                      {r.error && <p className="alerta-error">{r.error}</p>}
                      <div>
                        <p className="text-xs font-medium text-azul-noche">Qué piensa</p>
                        {r.reasoning ? (
                          <p className="mt-1 whitespace-pre-wrap text-xs font-normal leading-relaxed text-texto">{r.reasoning}</p>
                        ) : (
                          <p className="mt-1 text-xs font-normal text-gris">
                            {r.tokens_reasoning
                              ? `El modelo razonó (${nf.format(r.tokens_reasoning)} tokens de pensamiento), pero ${r.provider_code} no expone ese texto con salida estructurada.`
                              : `${r.provider_code} no expone el razonamiento con salida estructurada; con un conector Claude este panel muestra el pensamiento completo.`}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-azul-noche">Qué recibió (extracto)</p>
                        <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-white p-3 font-mono text-[11px] leading-relaxed text-texto ring-1 ring-inset ring-slate-200">{r.input_excerpt ?? '—'}</pre>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-azul-noche">Qué devolvió al siguiente agente</p>
                        <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-white p-3 font-mono text-[11px] leading-relaxed text-texto ring-1 ring-inset ring-slate-200">{r.output ? JSON.stringify(r.output, null, 2) : '—'}</pre>
                      </div>
                    </div>
                  </details>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Candado de publicación: items encontrados pero ya publicados en una edición
          aprobada/enviada — se muestran para transparencia (clave al testear a mano). */}
      {job?.status === 'done' && Array.isArray(job.detail?.ya_publicados) && (job.detail.ya_publicados as unknown[]).length > 0 && (
        <div className="tarjeta p-5">
          <p className="text-sm font-medium text-azul-noche">
            Ya publicados anteriormente ({(job.detail.ya_publicados as unknown[]).length})
          </p>
          <p className="mt-1 text-xs font-normal text-gris">
            Víctor los detectó de nuevo pero la regla de la casa impide re-publicarlos: cada hecho sale una sola vez
            (la única excepción es alerta → boletín de su semana).
          </p>
          <ul className="mt-3 flex flex-col gap-2">
            {(job.detail.ya_publicados as { titulo: string; edicion: string }[]).map((y, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm font-normal text-texto">
                <span className="chip mt-0.5 shrink-0 bg-amber-50 text-amber-700 ring-amber-200">{y.edicion}</span>
                <span className="min-w-0">{y.titulo}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Fallas técnicas de la corrida: llamadas LLM que murieron o se rescataron truncadas.
          Una corrida con fallas NO se ve igual que una limpia, aunque haya terminado "done". */}
      {job && fallasLLM.length > 0 && (
        <div className="tarjeta border border-amber-200 bg-amber-50/60 p-5">
          <p className="text-sm font-medium text-amber-900">Fallas técnicas durante la corrida</p>
          <ul className="mt-2 flex flex-col gap-1.5">
            {fallasLLM.map((f, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-2 text-sm font-normal text-amber-900/90">
                <span className={`chip shrink-0 ${f.tipo === 'fallo' ? 'bg-red-50 text-red-700 ring-red-300' : 'bg-amber-100 text-amber-800 ring-amber-300'}`}>
                  {f.tipo === 'fallo' ? 'Fallo' : 'Rescate'} · {f.agente}{f.fuente ? ` (${f.fuente})` : ''}
                </span>
                <span className="min-w-0 break-words text-[13px]">{f.modelo}: {f.error}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Cierre sin novedades: la corrida es legítima pero no amerita edición ni correo.
          Si quedaron fuentes sin analizar, el cierre deja de ser tranquilo: se advierte. */}
      {job?.status === 'done' && !job.edition_id && (
        <div
          className={`tarjeta flex items-start gap-4 border p-5 ${
            fuentesFallidas.length > 0 ? 'border-amber-300 bg-amber-50' : 'border-[#98D0EB]/70 bg-[#F2F9FD]'
          }`}
        >
          <span
            aria-hidden="true"
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-medium ${
              fuentesFallidas.length > 0 ? 'bg-amber-200/70 text-amber-800' : 'bg-[#98D0EB]/45 text-azul-profundo'
            }`}
          >
            {fuentesFallidas.length > 0 ? '⚠' : '✓'}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-azul-noche">
              {fuentesFallidas.length > 0 ? 'Sin novedades — pero la revisión quedó incompleta' : 'Sin novedades urgentes'}
            </p>
            <p className="mt-1 text-sm font-normal leading-relaxed text-gris">
              {(() => {
                const revisadas = (n1?.totalFuentes ?? 0) - fuentesFallidas.length;
                const revisados = n1?.itemsExtraidos
                  ? ` y evaluó ${n1.itemsExtraidos} ${n1.itemsExtraidos === 1 ? 'item' : 'items'}`
                  : '';
                if (fuentesFallidas.length > 0) {
                  return `El equipo revisó ${revisadas} de ${n1?.totalFuentes ?? '?'} fuentes${revisados} sin encontrar novedades, pero ${fuentesFallidas.join(', ')} descargó y NO pudo ser analizada (falla del modelo). Este "sin novedades" no cubre esa fuente: conviene repetir la corrida o revisarla a mano.`;
                }
                const fuentes = n1?.totalFuentes ? `las ${n1.totalFuentes} fuentes` : 'las fuentes';
                return `El equipo revisó ${fuentes}${revisados}, pero ninguno amerita publicación (prioridad ALTA o MEDIA). No se generó edición ni se enviará correo; el monitoreo queda registrado en esta corrida.`;
              })()}
            </p>
          </div>
        </div>
      )}

      {/* La noticia final */}
      {job?.status === 'done' && job.edition_id && (
        <div className="tarjeta overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 pb-1 pt-4">
            <p className="text-sm font-medium text-azul-noche">La noticia generada</p>
            <div className="flex gap-2">
              {job.edition_id && htmlNoticia && (
                <button
                  type="button"
                  disabled={descargandoPdf}
                  onClick={() => {
                    setDescargandoPdf(true);
                    void descargarArchivo(`/api/editions/${job.edition_id}/pdf`, 'AMBEC-edicion.pdf')
                      .catch(() => undefined)
                      .finally(() => setDescargandoPdf(false));
                  }}
                  className="btn-secundario !h-8 !px-3.5 !text-[13px] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {descargandoPdf ? 'Generando PDF…' : 'Descargar PDF'}
                </button>
              )}
              <Link to="/newsletter" className="btn-primario !h-8 !px-3.5 !text-[13px]">Revisar y aprobar</Link>
            </div>
          </div>
          {htmlNoticia ? (
            <iframe title="Vista previa de la noticia" srcDoc={htmlNoticia} sandbox="" className="mt-3 h-[720px] w-full border-0 bg-[#EEF2F7]" />
          ) : (
            <p className="px-5 py-6 text-sm font-normal text-gris">
              {job.edition_id ? 'Cargando la vista previa…' : 'La corrida terminó sin items publicables — no se generó noticia.'}
            </p>
          )}
        </div>
      )}

      {job?.status === 'error' && (
        <div role="alert" className="alerta-error">
          La corrida falló: {job.error ?? 'error desconocido'}. Puedes volver a generar desde Newsletter.
        </div>
      )}
    </div>
  );
}
